import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	fetchOtherRetailers,
	saveOtherRetailers,
	removeOtherRetailer,
	scanOtherRetailers,
	clearScan,
	anchorKey,
	type AnchorType,
} from 'slices/otherRetailerSlice'

/**
 * Everything a map surface needs to show and curate Other Retailers.
 *
 * Linear: MYD-484.
 *
 * This exists so the second surface is a ten-line change rather than a second
 * copy. `LocationsMapPanel` and the deferred Evaluations Sites tab have
 * entirely different layouts but identical behaviour here: fetch by anchor,
 * open a scan dialog, narrow the result by the outermost enabled ring.
 *
 * The ring filter is applied ONCE, here, and the same `visibleItems` array is
 * handed to both the sidebar list and the map layer. Filtering in two places
 * is how a list and a map start disagreeing about what is within a mile.
 */

export interface UseOtherRetailersArgs {
	anchorType?: AnchorType
	anchorId?: string
	/**
	 * The outermost enabled trade-area radius, or null when no rings are
	 * selected. Null means no narrowing: rings narrow, they do not gate.
	 */
	ringLimit?: number | null
	/** Skip the fetch entirely when the surface has no anchor to hang off. */
	enabled?: boolean
}

export function useOtherRetailers({
	anchorType,
	anchorId,
	ringLimit = null,
	enabled = true,
}: UseOtherRetailersArgs) {
	const dispatch = useDispatch<any>()
	const [isScanOpen, setIsScanOpen] = useState(false)

	const active = Boolean(enabled && anchorType && anchorId)
	const key = active ? anchorKey({ anchorType: anchorType!, anchorId: anchorId! }) : null

	const { entities, byAnchor, isLoading } = useSelector((state: any) => state.otherRetailers)

	useEffect(() => {
		if (!active) return
		dispatch(fetchOtherRetailers({ anchorType: anchorType!, anchorId: anchorId! }))
	}, [dispatch, active, anchorType, anchorId])

	const items = useMemo(() => {
		if (!key) return []
		return (byAnchor[key] || []).map((id: string) => entities[id]).filter(Boolean)
	}, [key, byAnchor, entities])

	/**
	 * A retailer with no computed distance is kept rather than hidden. The
	 * server recomputes distance against the anchor on every read, so a null
	 * here means the anchor lost its coordinates, and silently dropping the
	 * user's saved rows would look like data loss.
	 */
	const visibleItems = useMemo(() => {
		if (ringLimit == null) return items
		return items.filter((r: any) => r.distanceMiles == null || r.distanceMiles <= ringLimit)
	}, [items, ringLimit])

	const openScan = useCallback(() => setIsScanOpen(true), [])

	const closeScan = useCallback(() => {
		setIsScanOpen(false)
		dispatch(clearScan())
	}, [dispatch])

	const runScan = useCallback(
		(options: { radiusMiles?: number; categories?: string[]; namedChainsOnly?: boolean }) => {
			if (!active) return
			return dispatch(
				scanOtherRetailers({ anchorType: anchorType!, anchorId: anchorId!, ...options }),
			)
		},
		[dispatch, active, anchorType, anchorId],
	)

	const saveSelection = useCallback(
		async (selected: any[]) => {
			if (!active || selected.length === 0) return
			const result = await dispatch(
				saveOtherRetailers({ anchorType: anchorType!, anchorId: anchorId!, items: selected }),
			)
			if (!result.error) setIsScanOpen(false)
			return result
		},
		[dispatch, active, anchorType, anchorId],
	)

	const removeItem = useCallback(
		(item: any) => {
			/**
			 * `_id` OR `id`, and that is not defensive padding.
			 *
			 * `OtherRetailPanel` hands back an `OtherRetailItem`, whose id field is
			 * `id` because the panel is fed a mapped shape rather than the raw row.
			 * This guard originally tested `item._id` alone, so it was always
			 * undefined and every remove returned early in silence. The X looked
			 * like it worked and nothing ever happened.
			 */
			const id = item?._id ?? item?.id
			if (!active || !id) return
			return dispatch(
				removeOtherRetailer({ anchorType: anchorType!, anchorId: anchorId!, id }),
			)
		},
		[dispatch, active, anchorType, anchorId],
	)

	return {
		items,
		visibleItems,
		isLoading: active ? isLoading : false,
		isScanOpen,
		openScan,
		closeScan,
		runScan,
		saveSelection,
		removeItem,
		/** True when this surface can actually scan: no anchor, no feature. */
		isAvailable: active,
	}
}

export default useOtherRetailers
